/**
 * This is the default language.
 *
 * It should be used as the reference for which strings are to be defined in the app.
 *
 * Please maintain alphabetical order on variable name for ease of maintenance!
 */

export const nb = {
  apiDocumentation: 'API-dokumentasjon',
  apiDocVardef: 'Variabeldefinisjoner',
  apiDocKlass: 'Klassifikasjoner',
  appSubTitle: ['Her samler vi informasjon om våre datasett, variabler, klassifikasjoner og API-er på ett sted.'],
  appTitle: 'SSB Dataportal',

  authentication: {
    logIn: 'Logg inn',
    logInSsbEmployee: 'Logg inn som SSB ansatt',
    logOut: 'Logg ut',
    loginHeading: 'Innlogging',
    loginInfo:
      'Innlogging er kun tilgjengelig for ansatte i Statistisk sentralbyrå. Du kan fortsatt bruke tjenesten uten å logge inn.',
  },

  breadcrumbsLabel: 'Brødsmulesti',

  button: {
    removeFilter: 'Fjern alle filter',
  },

  by: 'av',

  classification: {
    about: {
      custodian: 'Ansvarlig',
      mail: 'E-post',
      publishedLanguages: 'Publiserte språk',
      basedOn: 'Basert på',
      langEN: 'Engelsk',
      langNB: 'Bokmål',
      langNN: 'Nynorsk',
      langNO: 'Norsk',
      legalBasis: 'Lovhjemmel',
      publications: 'Publikasjoner',
      unitTypes: 'Enhetstyper',
      noChanges: 'Ingen beskrivelse av endringer innenfor denne versjonen.',
      changelog: 'Endringslogg',
      date: 'Dato',
      comment: 'Kommentar',
    },
    filterLevels: 'Filtrer på nivå',
    labelPlural: 'Klassifikasjoner',
    labelSingular: 'Klassifikasjon',
    label: 'Klassifikasjon',
    language: {
      notSelectedLanguage: 'Denne klassifikasjonen er ikke tilgjengelig på valgt språk',
      missingInSelectedLanguage: 'Denne klassifikasjonen mangler innhold på valgt språk, velg et annet språk.',
      contentChangelog: 'Endringsloggen er kun tilgjengelig på norsk',
    },
    type: 'Type',
    codelist: 'Kodeliste',
    standard: 'Standard',
    emailPlaceholder: 'Din e-postadresse',
    subscribe: 'Abonner',
    subscribeMessageError: 'Det oppstod en feil under registering',
    subscribeMessageAlready: 'Du er allerede abonnent',
    subscribeSubmit: 'Send inn',
    subscribeInfo: 'Abonner på oppdateringer for denne klassifikasjonen',
    subscribeMessageSuccess: 'Du vil motta en mail. Følg instruksjonene i mailen for å starte ditt abonnement.',
    subscribeMessageInvalidEmail: 'Skriv inn en gyldig e-postadresse',
    download: {
      button: 'Last ned',
      formatLabel: 'Format',
      languageLabel: 'Språk',
      levelLabel: 'Nivå',
      allLevels: 'Alle nivåer',
      headingCodes: 'Last ned koder til fil',
      headingChanges: 'Last ned endringer til fil',
      headingCorrespondence: 'Last ned korrespondansen til fil',
      copyLink: 'Kopier lenke',
      linkCopied: 'Lenke kopiert.',
      confirm: 'Last ned fil',
      inProgress: 'Laster ned ...',
      error: 'Kunne ikke laste ned filen. Prøv igjen.',
    },
    correspondence: {
      heading: 'Korrespondanser',
      info: 'Korrespondansetabeller viser sammenhengen mellom to ulike kodeverk.',
      none: 'Denne versjonen har ingen korrespondanser',
      fromLevel: 'Nivå',
      toLevel: 'Nivå',
      owner: 'Eier',
      id: 'ID',
      ownerSection: 'Eierseksjon',
      responsible: 'Ansvarlig',
      codeSummary:
        'Korrespondansen kobler {sourceCount} koder fra «{sourceName}» til {targetCount} koder fra «{targetName}».',
      tableLabel: 'Korrespondansetabell',
      noTarget: 'Ingen tilsvarende kode',
    },
    variant: {
      id: 'ID',
      description: 'Beskrivelse',
      responsible: 'Ansvarlig',
      ownerSection: 'Eierseksjon',
      noVariants: 'Denne versjonen har ingen varianter',
      numberOfCodesAndLevels: 'Varianten inneholder {numberOfCodes} koder fordelt over {numberOfLevels} {level}.',
      variantHeading: 'Varianter',
      variantInfo:
        'En variant baserer seg på en klassifikasjonsversjon, og innebærer en omgruppering, utvidelse eller reduksjon av elementene i denne.',
    },
  },
  classificationDetails: {
    codes: 'Koder',
    details: 'Detaljer',
    changes: 'Endringer',
    versions: 'Velg versjon',
    correspondences: 'Korrespondanser',
    variants: 'Varianter',
  },
  close: 'Lukk',
  codeTree: {
    label: 'Kodeliste',
    expand: 'Vis underkoder for',
    collapse: 'Skjul underkoder for',
    selectCode: 'Velg kode',
    filterLabel: 'Filtrer på kode eller navn',
    filterPlaceholder: 'Filtrer på kode eller navn',
    expandAll: 'Åpne alle',
    collapseAll: 'Lukk alle',
    codeColumn: 'Kode',
    nameColumn: 'Navn',
    back: 'Tilbake',
    notesButtonLabel: 'Vis tilleggsinformasjon for',
    showShortNames: 'Vis kortnavn',
    shortNameColumn: 'Kortnavn',
    notesColumn: 'Tilleggsinformasjon',
  },
  codeSnippet: {
    codeExample: 'Kodeeksempel',
    daplaManual: 'Dapla-manualen',
    getVariableDefinition: 'Hent variabeldefinisjon med',
    linkToPyPiPackage: 'dapla-toolbelt-metadata (pypi.org)',
    daplaLab: 'Dapla Lab',
  },

  cookieBanner: {
    closeButtonLabel: 'Lukk knapp',
    label: 'Informasjonskaplser kunngjøring',
    message: 'Vi lagrer nødvendige informasjonskapsler som gjør at nettsiden fungerer og er trygg.',
  },
  contact: {
    label: 'Kontakt',
  },
  copy: {
    code: 'Kopier kode',
    copied: 'Kopiert',
    id: 'Kopier ID',
    shortName: 'Kopier kortnavn',
    filePath: 'Kopier gs:// sti',
  },

  dataCoverageTimeline: {
    availableDataFileLabel: 'tilhørende data fil',
    availableDataFileLabelPlural: 'tilhørende data filer',
    filePathLabel: 'Filsti',
    labelBimesterPrefix: 'B',
    labelFullYear: 'Hele',
    labelHalfYearPrefix: 'H',
    labelQuarterPrefix: 'K',
    labelTriannualPrefix: 'T',
    monthsShort: ['jan.', 'feb.', 'mars', 'apr.', 'mai', 'juni', 'juli', 'aug.', 'sep.', 'okt.', 'nov.', 'des.'],
    statusDataPresent: 'Data finnes',
    statusMissingTargetSegment: 'Ingen fil med data for perioden',
    tooltipStatusDataPresent: 'Fil med data finnes',
    tooltipStatusMissingTargetSegment: 'Ingen fil med data for perioden',
  },
  dataProduct: {
    labelPlural: 'Dataprodukter',
  },
  dataProductDetail: {
    dataProductFilters: 'Dataproduktfiltre',
    dataset: 'Datasett',
    sortByMostNamingStandardViolations: 'Flest navnestandardavvik',
    noDatasetAvailable: 'Ingen datasett tilgjengelig',
  },
  datasetDetail: {
    aboutDataset: 'Om datasettet',
    dataProduct: 'Dataprodukt',
    bucket: 'Bøtte',
    datasetState: 'Datatilstand',
    assessment: 'Vurdering',
    responsible: 'Ansvarlig team',
    id: 'ID',
    namingStandardViolations: 'Antall navnestandardavvik',
    dataFiles: 'Datafiler',
    dataCoverageTimeline: 'Tilgjengelige perioder',
  },
  editing: {
    created: 'Opprettet',
    updated: 'Sist oppdatert',
  },
  error: {
    classificationDetailsTabs: {
      notFoundCodes: 'Koder ikke funnet',
      notFoundAboutClassification: 'Om klassifikasjonen ikke funnet',
      notFoundChanges: 'Kodeendringer ikke funnet',
      notFoundVersions: 'Versjoner ikke funnet',
      notFoundCorrespondences: 'Korrespondanser ikke funnet',
      notFoundVariants: 'Varianter ikke funnet',
    },
    somethingWentWrong: 'Beklager, noe gikk galt.',
    unauthorized: 'Beklager, du har ikke tilgang.',
    technicalProblemsTitle: 'Vi har tekniske problemer',
    technicalProblemsMessage: 'Dette skyldes ikke noe du gjorde. Vent litt og prøv igjen.',
    reloadPage: 'Last siden på nytt',
    goBack: 'Gå tilbake',
    goHome: 'Gå til forsiden',
    helpTitle: 'Du kan prøve å:',
    helpReload: 'vente litt og laste siden på nytt',
    helpBack: 'gå tilbake til forrige side',
    helpHome: 'gå til forsiden',
    helpChangeFilters: 'endre søk eller filtre',
    helpRegisterChangeTable: 'registrere en endringstabell',
    missingChangeTable: '{sourceName} har ingen endringstabell mot: {targetName}',
    statusCode: 'Feilkode',
    referenceCode: 'Referanse',
    supportPrefix: 'Har problemet vart en stund, kan du',
    supportLinkText: 'kontakte oss',
    brokenLinkMailSubject: 'Ødelagt lenke i SSB dataportal',
    brokenLinkMailBody: (path: string) =>
      [
        'Hei, jeg vil melde fra om en mulig ødelagt lenke i SSB Dataportal.',
        '',
        `Side: ${path}`,
        '',
        'Beskrivelse av hva som ikke fungerte:',
      ].join('\n'),
    notFoundHelpListClassificationDetails: [
      'sjekke at du har riktig klassifikasjon-id i lenken',
      'gå til oversikten over klassifikasjoner',
      'gå til forsiden',
    ],
    notFoundHelpListClassificationId: [
      'sjekke at du har riktig klassifikasjon-id i lenken',
      'gå til oversikten over klassifikasjoner',
    ],
    notFoundDataProductDetails: 'Dataprodukt ikke funnet',
    notFoundTitleDataProductDetails: 'Dataprodukt ikke funnet',
    notFoundMessageDataProductDetails:
      'Er det skrivefeil i lenken? Eller har dataproduktet blitt slettet eller flyttet?',
    notFoundHelpListDataProductDetails: [
      'sjekke at du har riktig dataprodukt-id i lenken',
      'gå til oversikten over dataprodukter',
    ],
    notFoundTitleDataset: 'Datasett ikke funnet',
    notFoundMessageDataset: 'Er det skrivefeil i lenken? Eller har datasettet blitt slettet eller flyttet?',
    notFoundHelpListDataset: [
      'sjekke at du har riktig datasett-id i lenken',
      'gå til oversikten over datasett',
      'gå til oversikten over dataprodukter',
    ],
    notFoundTitleClassificationDetails: 'Klassifikasjon ikke funnet',
    notFoundMessageClassificationDetails:
      'Er det skrivefeil i lenken? Eller har klassifikasjonen blitt slettet eller flyttet?',
    notFoundTitleVersionDetails: 'Versjon ikke funnet',
    notFoundMessageVersionDetails: 'Er det skrivefeil i lenken? Eller har versjonen blitt slettet eller flyttet?',
    notFoundHelpListVersionDetails: [
      'sjekke at du har riktig versjons-id i lenken',
      'gå tilbake til klassifikasjonen',
      'gå til oversikten over klassifikasjoner',
    ],
    notFoundTitleVariantDetails: 'Variant ikke funnet',
    notFoundMessageVariantDetails: 'Er det skrivefeil i lenken? Eller har varianten blitt slettet eller flyttet?',
    notFoundHelpListVariantDetails: [
      'sjekke at du har riktig variant-id i lenken',
      'gå til oversikten over varianter',
      'gå til oversikten over klassifikasjoner',
    ],
    notFoundTitleCorrespondenceDetails: 'Korrespondansetabell ikke funnet',
    notFoundMessageCorrespondenceDetails:
      'Er det skrivefeil i lenken? Eller har korrespondansetabellen blitt slettet eller flyttet?',
    notFoundHelpListCorrespondenceDetails: [
      'sjekke at du har riktig korrespondansetabell-id i lenken',
      'gå til oversikten over korrespondanser',
      'gå til oversikten over klassifikasjoner',
    ],
    notFoundTitle: 'Siden finnes ikke',
    notFoundMessage: 'Siden kan være flyttet, slettet eller lenken kan være feil.',
    notFoundTitleVariableDetails: 'Variabeldefinisjon ikke funnet',
    notFoundMessageVariableDetails:
      'Er det skrivefeil i lenken? Eller har variabeldefinisjonen blitt slettet eller flyttet?',
    notFoundHelpListVariableDetails: [
      'sjekke at du har riktig variabeldefinisjon-kortnavn i lenken',
      'gå til oversikten over variabeldefinisjoner',
      'gå til forsiden',
    ],
    reportBrokenLink: 'Meld fra om ødelagt lenke',
  },
  from: 'Fra',
  info: {
    aboutDataportal: {
      body: [
        'Målet er å gjøre det enklere å finne, forstå og bruke SSBs data på en korrekt og effektiv måte.',
        'I denne første versjonen kan du utforske SSBs variabeldefinisjoner. Portalen vil gradvis utvides med oversikt over datasett, klassifikasjoner og API-er, slik at du kan se hele sammenhengen fra konsept til ferdig datafil. Ved å koble definisjoner fra Vardef med kodeverk fra Klass og dokumentasjon om datasett, etablerer vi en "felles sannhet" som sikrer at vi alltid tolker og bruker dataene våre på samme måte.',
      ],
      title: 'Om SSB Dataportal',
      toggle: 'Hvorfor SSB Dataportal',
    },
    datasetPrototypeIntro: 'Under utvikling',
    datasetPrototypeInfo:
      'Denne siden beskriver et utvalg av data i SSB. Utvikling fortsetter med flere typer data og mer detaljerte beskrivelser.',
    feedbackTitle: 'Del dine erfaringer!',
    feedbackBody: 'Vi jobber kontinuerlig med å forbedre dataportalen og du kan hjelpe oss ved å fylle ut',
    feedbackForm: 'tilbakemeldingsskjema for SSB Dataportal',
    footerAboutPage: 'Om nettstedet',
    footerContact: 'Kom i kontakt',
    footerAccessibilityStatement: 'Tilgjengelighetserklæring',
    footerPrivacyStatement: 'Personvernerklæring',
    landingPageSubTitle: 'Din inngang til SSBs felles kunnskap om data',
  },
  loading: {
    filters: 'Laster filtere...',
    results: 'Laster resultater...',
  },
  migrationClassifications: {
    header: 'Klass er flyttet fra ssb.no/klass',
    info: 'Du er nå på den nye tjenesten Klass i SSB Dataportal. Oppdater gjerne bokmerker og lagrede lenker.',
  },
  migrationVariableDefinitions: {
    header: 'Migrering av variabeldefinisjoner pågår',
    info: 'Vi er i gang med å flytte variabeldefinisjoner til SSB Dataportal. Inntil arbeidet er fullført vil noe innhold fortsatt ligge på den gamle siden.',
    linkText: 'Finn flere variabeldefinisjoner på ssb.no',
  },
  navigateHome: 'Naviger til hjemmesiden',
  next: 'Neste',
  no: 'Nei',
  noDataPlaceholder: '—',
  on: 'på',
  opensInNewTab: 'åpnes i ny fane',
  owner: {
    daplaTeam: 'Dapla Team',
    groups: 'Grupper',
  },
  pageTitle: {
    classifications: 'Klassifikasjoner',
    dataProducts: 'Dataprodukter',
    variableDefinitions: 'Variabeldefinisjoner',
  },
  previous: 'Forrige',
  products: {
    assessment: {
      filterLabel: 'Verdivurdering',
      open: 'Åpen',
      protected: 'Skjermet',
      sensitive: 'Sensitiv',
    },
    datasetState: {
      processedData: 'Klargjorte data',
      outputData: 'Utdata',
      inputData: 'Inndata',
      statistics: 'Statistikk',
      sourceData: 'Kildedata',
    },
    other: 'Annen dataprodukt',
    statistic: 'Statistikkprodukt',
    storageCategory: {
      filterLabel: 'Lagringskategori',
      shared: 'Delt',
      product: 'Produkt',
      source: 'Kilde',
    },
    typeFilterLabel: 'Dataprodukttype',
    unknown: 'Ukjent produkttype',
  },
  search: {
    hits: 'treff',
    label: 'Søk',
    noHits: 'Ditt søk ga ingen treff',
    variableDefinitions: 'Søketreff variabeldefinisjoner',
    classifications: 'Søketreff klassifikasjoner',
    textFilter: {
      label: 'Filtrer på navn',
      tagLabel: 'Navn:',
      inputId: 'søk-input',
    },
    filter: {
      close: 'Lukk filter',
      label: 'Filter',
      filterAndSearch: 'Filter og søk',
      open: 'Åpne filter',
    },
    sort: {
      label: 'Sortering',
      lastUpdatedFirst: 'Sist endret først',
      titleAlphabeticalAsc: 'Tittel A-Å',
      titleAlphabeticalDesc: 'Tittel Å-A',
    },
  },

  filterTag: {
    remove: 'Fjern',
    listLabel: 'Liste over valgte filter',
    sectionLabel: 'Aktive søkefilter',
  },

  ssbDataportal: 'SSB Dataportal',
  statisticsNorway: 'Statistisk sentralbyrå',

  status: {
    draft: 'Utkast',
    label: 'Status',
    publishedExternal: 'Publisert eksternt',
    publishedInternal: 'Publisert internt',
  },

  subjectArea: 'Statistikkområde',
  subjectFields: 'Statistikkområder',

  tabs: {
    ariaLabel: 'Navigasjon kataloger',
    classifications: 'Klassifikasjoner',
    dataProducts: 'Dataprodukter',
    variableDefinitions: 'Variabeldefinisjoner',
  },

  to: 'Til',
  unitTypes: 'Enhetstyper',
  yes: 'Ja',

  validity: {
    validityPeriods: 'Gyldighetsperioder',
    validityPeriodsInfo:
      'Dersom en variabeldefinisjon endrer betydning kan man avgrense de ulike betydningene til tidsperioder. Her kan man bytte mellom gyldighetsperioder ved å velge datoen perioden gjelder fra.',
    chooseValidityPeriod: 'Velg gyldighetsperiode',
    validFrom: 'Gyldig fra',
    validTo: 'Gyldig til',
  },

  // Labels specific to variable definitions
  variableDefinition: {
    aboutVariable: 'Om variabelen',
    comment: 'Kommentar',
    contact: 'Kontakt',
    documentation: 'Dokumentasjon',
    externalPersonalData: 'Sensitive personopplysninger',
    id: 'ID',
    internalPersonalData: 'Inneholder særlige kategorier av personopplysninger',
    labelPlural: 'Variabeldefinisjoner',
    mail: 'Mail',
    owner: 'Eier',
    unitTypeInfo:
      'En enhetstype er typen av objekter (enheter) som det lages statistikk om, f.eks. person, foretak og valg',
    shortName: 'Kortnavn',
  },
  versions: {
    name: 'Navn',
    invert: 'Inverter tabell',
    isNotValid: 'Versjonen er ikke gyldig på dagens dato.',
    noChanges: 'Ingen kodeendringer finnes for den aktuelle versjonen.',
    numberOfCodesAndLevels: 'Versjonen inneholder {numberOfCodes} koder fordelt over {numberOfLevels} {level}.',
    level: 'nivå',
    levelPlural: 'nivåer',
    codeChangesForVersion: '{numberOfChanges} kodeendringer fra forrige versjon.',
    codeChangesTableLabel: 'Tabell over kodeendringer',
  },
};

export type Translation = typeof nb;
